import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSkill } from "@/hooks/use-skill";
import { useUpdateSkill } from "@/hooks/use-skill-mutations";
import { ALL_CATEGORIES } from "@/lib/skills/categories";
import { categoryMeta, DEFAULT_INSTALL_STEPS } from "@/lib/skills/constants";
import { cn } from "@/lib/utils";

const schema = z.object({
  title: z.string().trim().min(1, "Title is required").max(120),
  desc: z.string().trim().min(1, "A short description is required").max(280),
  categories: z
    .array(z.enum(["UI/UX", "Security", "Efficiency"]))
    .min(1, "Pick at least one category")
    .max(3),
  whatIs: z.string().trim().min(1, "Required").max(4000),
  whyImportant: z.string().trim().min(1, "Required").max(4000),
  notable: z.string().trim().max(4000),
  install: z.string().trim().min(1, "Required").max(500),
  notes: z.string().trim().max(500).optional(),
  examples: z
    .array(
      z.object({
        scenario: z.string().trim().min(1, "Scenario is required").max(120),
        body: z.string().trim().min(1, "Example body is required").max(2000),
      }),
    )
    .max(10),
  // Modeled as rows of objects so useFieldArray gets stable ids; flattened on submit.
  installSteps: z
    .array(z.object({ value: z.string().trim().min(1, "Step can't be empty").max(500) }))
    .max(12),
});
type Values = z.infer<typeof schema>;

const EMPTY: Values = {
  title: "",
  desc: "",
  categories: [],
  whatIs: "",
  whyImportant: "",
  notable: "",
  install: "",
  notes: "",
  examples: [],
  installSteps: [],
};

export function EditMetadataDialog({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  // Fetch fresh on open rather than trusting props (frontend.md rule).
  const { data } = useSkill(slug, { enabled: open });
  const update = useUpdateSkill(slug);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
    values: data
      ? {
          title: data.title,
          desc: data.desc,
          categories: data.cat,
          whatIs: data.whatIs,
          whyImportant: data.whyImportant,
          notable: data.notable,
          install: data.install,
          notes: data.notes || "",
          examples: data.examples,
          // Seed from the current steps; fall back to the generic defaults the
          // detail page shows so editing starts from what the user actually sees.
          installSteps: (data.installSteps.length > 0
            ? data.installSteps
            : DEFAULT_INSTALL_STEPS
          ).map((value) => ({ value })),
        }
      : undefined,
  });

  const examples = useFieldArray({ control: form.control, name: "examples" });
  const steps = useFieldArray({ control: form.control, name: "installSteps" });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit metadata</DialogTitle>
          <DialogDescription>
            Update this skill's details. Changes are visible to everyone immediately.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((v) =>
              update.mutate(
                {
                  ...v,
                  notes: v.notes || undefined,
                  installSteps: v.installSteps.map((s) => s.value),
                },
                { onSuccess: () => onOpenChange(false) },
              ),
            )}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="desc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Short description</FormLabel>
                  <FormDescription>One line shown on the skill card.</FormDescription>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="categories"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categories</FormLabel>
                  <FormDescription>Assign at least one.</FormDescription>
                  <div className="flex flex-wrap gap-2">
                    {ALL_CATEGORIES.map((c) => {
                      const on = field.value.includes(c);
                      const Icon = categoryMeta(c).icon;
                      return (
                        <button
                          key={c}
                          type="button"
                          aria-pressed={on}
                          onClick={() =>
                            field.onChange(
                              on ? field.value.filter((x) => x !== c) : [...field.value, c],
                            )
                          }
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                            on
                              ? "border-fg-link bg-bg-2 text-fg-1"
                              : "border-border-2 text-fg-2 hover:bg-bg-2",
                          )}
                        >
                          <Icon className="size-4" />
                          {c}
                        </button>
                      );
                    })}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="whatIs"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What is this skill?</FormLabel>
                  <FormControl>
                    <Textarea className="min-h-[90px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="whyImportant"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Why is this important?</FormLabel>
                  <FormControl>
                    <Textarea className="min-h-[90px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notable"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Notable items <span className="font-normal text-fg-3">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[70px]"
                      placeholder="Anything worth calling out — caveats, dependencies, limitations."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="install"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Install command</FormLabel>
                  <FormControl>
                    <Input className="font-mono" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Custom install notes <span className="font-normal text-fg-3">(optional)</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="Any special setup or prerequisites." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <FormLabel>Install instructions</FormLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => steps.append({ value: "" })}
                >
                  <Plus className="size-4" />
                  Add step
                </Button>
              </div>
              <FormDescription>Numbered steps shown beneath the install command.</FormDescription>
              {steps.fields.map((f, i) => (
                <div key={f.id} className="flex items-start gap-2">
                  <span className="mt-2 grid size-5 shrink-0 place-items-center rounded-full bg-bg-3 text-xs font-semibold text-fg-2">
                    {i + 1}
                  </span>
                  <FormField
                    control={form.control}
                    name={`installSteps.${i}.value` as const}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Textarea className="min-h-[44px]" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove step"
                    onClick={() => steps.remove(i)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <FormLabel>
                  Examples <span className="font-normal text-fg-3">(optional)</span>
                </FormLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => examples.append({ scenario: "", body: "" })}
                >
                  <Plus className="size-4" />
                  Add example
                </Button>
              </div>
              <FormDescription>Concrete scenarios showing the skill in action.</FormDescription>
              {examples.fields.length === 0 ? (
                <p className="text-sm text-fg-3">No examples yet.</p>
              ) : (
                examples.fields.map((f, i) => (
                  <div
                    key={f.id}
                    className="flex flex-col gap-2 rounded-[12px] border border-border-1 p-3"
                  >
                    <div className="flex items-center gap-2">
                      <FormField
                        control={form.control}
                        name={`examples.${i}.scenario` as const}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <Input placeholder="Scenario (e.g. Reviewing a PR)" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove example"
                        onClick={() => examples.remove(i)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                    <FormField
                      control={form.control}
                      name={`examples.${i}.body` as const}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              className="min-h-[70px]"
                              placeholder="What the skill does in this scenario."
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                ))
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={update.isPending || !data}>
                {update.isPending ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
